import {Component, DoCheck, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, Self} from '@angular/core';
import {NgControl} from '@angular/forms';
import {FormGroupBaseComponent} from '../../formgroupbase.component';
import {JBMSelectOption} from '../../JBMSelect/JBMSelect.component';
import {Subscription} from 'rxjs';
import {StringIDdisplay} from '../../../../types';
import {JBMTableDisplayValue} from '../../../../JBMTable/types';

@Component({
  selector: 'jbm-array-autocomplete',
  templateUrl: './JBMArrayAutocomplete.component.html',
  styleUrls: ['./JBMArrayAutocomplete.component.scss']
})
export class JBMArrayAutocompleteComponent extends FormGroupBaseComponent implements OnInit, OnChanges, DoCheck, OnDestroy {
  @Input() options: JBMSelectOption[]=[];
  @Input() maxlength: number=0;
  @Input() placeholder: string='';
  @Input() stringIDdisplay: StringIDdisplay=StringIDdisplay.none;
  @Input() displayValues: JBMTableDisplayValue[] = [];
  @Output() onBlur = new EventEmitter();
  @Output() onChange = new EventEmitter();
  @Output() onSelect = new EventEmitter();
  @Output() optionSelect = new EventEmitter<JBMSelectOption>();

  dataListId: string='';
  displayValue: string='';
  filteredOptions: JBMSelectOption[]=[];
  listOpen: boolean=false;
  activeOptionIndex: number=-1;
  valueChanges$: Subscription;
  readonly stringIDdisplayType = StringIDdisplay;
  private lastControlValue: any;
  private lastOptionsLength: number=0;

  constructor(@Self() public ngControl: NgControl, private eRef: ElementRef) {
    super(ngControl);
  }

  ngOnInit(): void {
    if(this.options==undefined) this.options=[];
    if(this.maxlength==undefined) this.maxlength=0;
    if(this.placeholder==undefined) this.placeholder='';
    if(this.displayValues==undefined) this.displayValues=[];
    super.ngOnInit();
    this.dataListId = `${this.id}-autocomplete-options`;
    this.updateDisplayValue();
    this.valueChanges$ = this.ngControl.control.valueChanges.subscribe(() => this.updateDisplayValue());
  }
  ngOnChanges(): void {
    this.updateDisplayValue();
  }
  ngDoCheck(): void {
    const controlValue = this.ngControl.control.value;
    const optionsLength = (this.options || []).length;
    if(controlValue!=this.lastControlValue || optionsLength!=this.lastOptionsLength) {
      this.updateDisplayValue(controlValue);
    }
  }
  getDataValue() {
    const selected = this.getSelectedOption();
    if(selected) return this.getOptionDisplayValue(selected);
    return super.getDataValue();
  }
  getInputValue() {
    const selected = this.getSelectedOption();
    if(selected) return this.getOptionDisplayValue(selected);
    return this.ngControl.control.value || '';
  }
  updateDisplayValue(value: any=this.ngControl.control.value) {
    const selected = this.getSelectedOption(value);
    this.displayValue = selected ? this.getOptionDisplayValue(selected) : value || '';
    this.lastControlValue = value;
    this.lastOptionsLength = (this.options || []).length;
    this.setFilteredOptions(this.displayValue);
  }
  getSelectedOption(value: any=this.ngControl.control.value): JBMSelectOption {
    return (this.options || []).find((o: any) => o.key==value || o.value==value);
  }
  getOptionByInputValue(value: string): JBMSelectOption {
    return (this.options || []).find((o: any) => this.getOptionDisplayValue(o)==value || o.value==value || o.key==value);
  }
  getOptionByKeyValue(value: string): JBMSelectOption {
    const key = value.toString().trim();
    return this.getLookupOptions().find((o: any) => o.key!=undefined && o.key.toString()===key);
  }
  getLookupOptions(): JBMSelectOption[] {
    return (this.options || []).filter((o: any) => !!o.key || !!o.value);
  }
  setFilteredOptions(value: string='') {
    const search = value.toString().toLowerCase();
    this.filteredOptions = this.getLookupOptions().filter((o: any) => this.getOptionDisplayValue(o).toLowerCase().indexOf(search)>=0);
    this.activeOptionIndex = this.filteredOptions.length>0 ? 0 : -1;
  }
  hideList() {
    this.listOpen = false;
    this.activeOptionIndex = -1;
  }
  input(event: any) {
    const input = event.target as HTMLInputElement;
    this.displayValue = input.value;
    this.setFilteredOptions(input.value);
    this.listOpen = this.filteredOptions.length>0;
  }
  keyDown(event: KeyboardEvent) {
    if(event.key==='ArrowDown') {
      event.preventDefault();
      if(!this.listOpen) {
        this.setFilteredOptions(this.displayValue);
        this.listOpen = this.filteredOptions.length>0;
        return;
      }
      if(this.filteredOptions.length)
        this.activeOptionIndex = this.activeOptionIndex>=this.filteredOptions.length-1 ? 0 : this.activeOptionIndex+1;
    }

    if(event.key==='ArrowUp') {
      event.preventDefault();
      if(!this.listOpen) {
        this.setFilteredOptions(this.displayValue);
        this.listOpen = this.filteredOptions.length>0;
        return;
      }
      if(this.filteredOptions.length)
        this.activeOptionIndex = this.activeOptionIndex<=0 ? this.filteredOptions.length-1 : this.activeOptionIndex-1;
    }

    if(event.key==='Enter') {
      const option = this.getOptionByKeyValue(this.displayValue);
      if(option) {
        event.preventDefault();
        this.selectOption(option);
        return;
      }
    }

    if(event.key==='Enter' && this.listOpen && this.activeOptionIndex>-1) {
      event.preventDefault();
      this.selectOption(this.filteredOptions[this.activeOptionIndex]);
    }

    if(event.key==='Escape') {
      this.hideList();
    }
  }
  blur(event: any) {
    this.ngControl.control.markAsTouched();
    setTimeout(() => this.hideList(), 150);
    this.onBlur.emit(event);
  }
  change(event: any) {
    this.onChange.emit(event);
  }
  selectOption(option: JBMSelectOption) {
    this.displayValue = this.getOptionDisplayValue(option);
    this.hideList();
    const selectEvent = {target: {value: option.key}};
    if(this.ngControl.control.value!=option.key)
      this.ngControl.control.setValue(option.key);
    this.optionSelect.emit(option);
    this.onSelect.emit(selectEvent);
  }
  selectLookupOption(option: JBMSelectOption, event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.selectOption(option);
  }
  writeValue(obj: any) {
    this.updateDisplayValue(obj);
  }
  getOptionLabel(option: JBMSelectOption): string {
    for(const d of this.displayValues) {
      if((option.value==d.dataValue || option.key==d.dataValue) && d.displayValue!==undefined)
        return d.displayValue;
    }
    return option.value;
  }
  getOptionDisplayValue(option: JBMSelectOption): string {
    const label = this.getOptionLabel(option);
    if(option.key===null || option.key===undefined || option.key==='')
      return label;

    if(this.stringIDdisplay===StringIDdisplay.displayIdBefore)
      return `#${option.key} ${label}`;

    if(this.stringIDdisplay===StringIDdisplay.displayIdAfter)
      return `${label} (#${option.key})`;

    return label;
  }
  @HostListener("document:click", ['$event'])
  clickedOut(event: any) {
    if(!this.eRef.nativeElement.contains(event.target))
      this.hideList();
  }
  @HostListener('document:keyup', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if(event.key=='Escape') this.hideList();
  }
  ngOnDestroy() {
    if(this.valueChanges$!=undefined) this.valueChanges$.unsubscribe();
  }
}
